/**
 * Cursor Management Utilities
 * 
 * Provides utility functions for cursor position management, multi-character
 * operations, and advanced cursor positioning scenarios.
 */

import { FieldType } from '../detection-patterns';
import { CursorPosition, CursorOperationContext, CursorOperationType } from '../cursor/cursor-manager';
import { SelectionInfo } from '../cursor/selection-handler';

/**
 * Multi-character operation types
 */
export enum MultiCharOperation {
  /** Paste operation with multiple characters */
  PASTE = 'paste',
  /** Bulk delete (backspace/delete multiple characters) */
  BULK_DELETE = 'bulk_delete',
  /** Multi-character replacement */
  REPLACE = 'replace',
  /** Auto-correction replacement */
  AUTOCORRECT = 'autocorrect',
  /** Drag and drop text */
  DRAG_DROP = 'drag_drop',
  /** Cut operation */
  CUT = 'cut'
}

/**
 * Multi-character operation context
 */
export interface MultiCharOperationContext {
  /** Type of multi-character operation */
  operationType: MultiCharOperation;
  /** Original text before operation */
  originalText: string;
  /** New text after operation */
  newText: string;
  /** Original cursor position */
  originalCursor: CursorPosition;
  /** Text selection if applicable */
  selection?: SelectionInfo;
  /** Field type */
  fieldType: FieldType;
  /** Characters that were inserted/deleted */
  changedCharacters: string;
  /** Position where change occurred */
  changePosition: number;
  /** Whether formatting was applied */
  formatApplied: boolean;
}

/**
 * Cursor boundary information
 */
export interface CursorBoundary {
  /** Position of the boundary */
  position: number;
  /** Type of boundary */
  type: 'delimiter' | 'word' | 'format' | 'field';
  /** Boundary character(s) */
  character: string;
  /** Whether cursor can be placed at this boundary */
  canPlace: boolean;
}

/**
 * Text analysis result for cursor positioning
 */
export interface TextAnalysis {
  /** Total length */
  length: number;
  /** Number of delimiters */
  delimiterCount: number;
  /** Delimiter positions */
  delimiterPositions: number[];
  /** Word boundaries */
  wordBoundaries: number[];
  /** Format boundaries */
  formatBoundaries: CursorBoundary[];
  /** Raw character count (excluding delimiters) */
  rawLength: number;
}

/**
 * Cursor Utilities Class
 * 
 * Provides utility functions for cursor management across different scenarios.
 */
export class CursorUtils {
  /**
   * Handle multi-character operation cursor positioning
   * 
   * @param context Multi-character operation context
   * @returns Calculated cursor position
   */
  static handleMultiCharOperation(context: MultiCharOperationContext): CursorPosition {
    switch (context.operationType) {
      case MultiCharOperation.PASTE:
        return CursorUtils.handlePasteOperation(context);
        
      case MultiCharOperation.BULK_DELETE:
        return CursorUtils.handleBulkDeleteOperation(context);
        
      case MultiCharOperation.REPLACE:
        return CursorUtils.handleReplaceOperation(context);
        
      case MultiCharOperation.AUTOCORRECT:
        return CursorUtils.handleAutocorrectOperation(context);
        
      case MultiCharOperation.DRAG_DROP:
        return CursorUtils.handleDragDropOperation(context);
        
      case MultiCharOperation.CUT:
        return CursorUtils.handleCutOperation(context);
        
      default:
        return CursorUtils.getFallbackPosition(context.newText);
    }
  }

  /**
   * Handle paste operation cursor positioning
   */
  private static handlePasteOperation(context: MultiCharOperationContext): CursorPosition {
    // For paste operations, place cursor at the end of pasted content
    if (context.selection?.hasSelection) {
      // Paste replaced selection - cursor at end of replacement
      const pasteLength = context.changedCharacters.length;
      const newPosition = context.selection.start + pasteLength;
      
      return {
        start: Math.min(newPosition, context.newText.length),
        end: Math.min(newPosition, context.newText.length),
        direction: 'none'
      };
    } else {
      // Paste at cursor position - cursor at end of paste
      const pasteLength = context.changedCharacters.length;
      const newPosition = context.changePosition + pasteLength;
      
      return {
        start: Math.min(newPosition, context.newText.length),
        end: Math.min(newPosition, context.newText.length),
        direction: 'none'
      };
    }
  }

  /**
   * Handle bulk delete operation cursor positioning
   */
  private static handleBulkDeleteOperation(context: MultiCharOperationContext): CursorPosition {
    // For bulk delete, cursor stays at the deletion start position
    const newPosition = Math.min(
      context.changePosition,
      context.newText.length
    );
    
    return {
      start: newPosition,
      end: newPosition,
      direction: 'none'
    };
  }

  /**
   * Handle text replacement operation
   */
  private static handleReplaceOperation(context: MultiCharOperationContext): CursorPosition {
    // Cursor at end of replacement
    const replacementLength = context.changedCharacters.length;
    const newPosition = context.changePosition + replacementLength;
    
    return {
      start: Math.min(newPosition, context.newText.length),
      end: Math.min(newPosition, context.newText.length),
      direction: 'none'
    };
  }

  /**
   * Handle autocorrect operation
   */
  private static handleAutocorrectOperation(context: MultiCharOperationContext): CursorPosition {
    // For autocorrect, place cursor at end of corrected word
    return CursorUtils.handleReplaceOperation(context);
  }

  /**
   * Handle drag and drop operation
   */
  private static handleDragDropOperation(context: MultiCharOperationContext): CursorPosition {
    // Place cursor at end of dropped text
    return CursorUtils.handlePasteOperation(context);
  }

  /**
   * Handle cut operation
   */
  private static handleCutOperation(context: MultiCharOperationContext): CursorPosition {
    // Similar to delete - cursor at cut position
    return CursorUtils.handleBulkDeleteOperation(context);
  }

  /**
   * Analyze text structure for cursor positioning
   * 
   * @param text Text to analyze
   * @param fieldType Field type for context
   * @returns Text analysis result
   */
  static analyzeTextStructure(text: string, fieldType: FieldType): TextAnalysis {
    const delimiters = CursorUtils.getDelimitersForFieldType(fieldType);
    const delimiterPositions: number[] = [];
    const wordBoundaries: number[] = [];
    const formatBoundaries: CursorBoundary[] = [];

    // Find delimiter positions
    for (let i = 0; i < text.length; i++) {
      if (delimiters.includes(text[i])) {
        delimiterPositions.push(i);
        formatBoundaries.push({
          position: i,
          type: 'delimiter',
          character: text[i],
          canPlace: false // Generally don't place cursor on delimiters
        });
      }
    }

    // Find word boundaries
    for (let i = 0; i <= text.length; i++) {
      const prevChar = i > 0 ? text[i - 1] : '';
      const currChar = i < text.length ? text[i] : '';
      
      const prevIsWord = /\w/.test(prevChar);
      const currIsWord = /\w/.test(currChar);
      
      if ((!prevIsWord && currIsWord) || (prevIsWord && !currIsWord)) {
        wordBoundaries.push(i);
        formatBoundaries.push({
          position: i,
          type: 'word',
          character: '',
          canPlace: true
        });
      }
    }

    // Calculate raw length (excluding delimiters)
    const rawLength = text.split('').filter(char => !delimiters.includes(char)).length;

    return {
      length: text.length,
      delimiterCount: delimiterPositions.length,
      delimiterPositions,
      wordBoundaries,
      formatBoundaries,
      rawLength
    };
  }

  /**
   * Find the best cursor position near a target position
   * 
   * @param targetPosition Desired cursor position
   * @param text Text content
   * @param fieldType Field type
   * @param direction Preferred search direction
   * @returns Best cursor position
   */
  static findBestCursorPosition(
    targetPosition: number,
    text: string,
    fieldType: FieldType,
    direction: 'left' | 'right' | 'nearest' = 'nearest'
  ): number {
    // Ensure position is within bounds
    const boundedPosition = Math.max(0, Math.min(targetPosition, text.length));
    
    const analysis = CursorUtils.analyzeTextStructure(text, fieldType);
    const delimiters = CursorUtils.getDelimitersForFieldType(fieldType);
    
    // If not on a delimiter, position is fine
    if (boundedPosition >= text.length || !delimiters.includes(text[boundedPosition])) {
      return boundedPosition;
    }

    // Find best alternative position
    let bestPosition = boundedPosition;
    
    if (direction === 'left' || direction === 'nearest') {
      // Search left
      for (let i = boundedPosition - 1; i >= 0; i--) {
        if (!delimiters.includes(text[i])) {
          bestPosition = i + 1;
          break;
        }
      }
    }
    
    if (direction === 'right' || (direction === 'nearest' && bestPosition === boundedPosition)) {
      // Search right
      for (let i = boundedPosition; i < text.length; i++) {
        if (!delimiters.includes(text[i])) {
          if (direction === 'nearest') {
            // Choose closer position
            const leftDistance = boundedPosition - (bestPosition === boundedPosition ? 0 : bestPosition);
            const rightDistance = i - boundedPosition;
            if (rightDistance <= leftDistance) {
              bestPosition = i;
            }
          } else {
            bestPosition = i;
          }
          break;
        }
      }
    }
    
    return Math.max(0, Math.min(bestPosition, text.length));
  }

  /**
   * Calculate cursor position change due to formatting
   * 
   * @param originalText Original text
   * @param formattedText Formatted text
   * @param originalPosition Original cursor position
   * @param fieldType Field type
   * @returns New cursor position
   */
  static calculateFormattingCursorChange(
    originalText: string,
    formattedText: string,
    originalPosition: number,
    fieldType: FieldType
  ): number {
    // Create character mapping
    const mapping = CursorUtils.createSimpleCharacterMapping(
      originalText,
      formattedText,
      fieldType
    );

    // Find mapped position
    for (let i = 0; i < mapping.length; i++) {
      const entry = mapping[i];
      if (entry.originalIndex === originalPosition) {
        return entry.newIndex;
      } else if (entry.originalIndex > originalPosition) {
        // Return position before this character
        return i > 0 ? mapping[i - 1].newIndex + 1 : 0;
      }
    }

    // Fallback to proportional positioning
    const proportion = originalPosition / Math.max(1, originalText.length);
    return Math.round(proportion * formattedText.length);
  }

  /**
   * Check if cursor position is valid for field type
   * 
   * @param position Cursor position
   * @param text Text content
   * @param fieldType Field type
   * @returns Whether position is valid
   */
  static isValidCursorPosition(
    position: number,
    text: string,
    fieldType: FieldType
  ): boolean {
    // Basic bounds check
    if (position < 0 || position > text.length) {
      return false;
    }

    // Check if position is on a delimiter (generally invalid)
    const delimiters = CursorUtils.getDelimitersForFieldType(fieldType);
    if (position < text.length && delimiters.includes(text[position])) {
      return false;
    }

    // Field-specific validations could be added here
    return true;
  }

  /**
   * Get safe cursor position range around a target
   * 
   * @param targetPosition Target cursor position
   * @param text Text content
   * @param fieldType Field type
   * @param range Search range
   * @returns Array of valid positions
   */
  static getSafeCursorPositions(
    targetPosition: number,
    text: string,
    fieldType: FieldType,
    range: number = 5
  ): number[] {
    const validPositions: number[] = [];
    const start = Math.max(0, targetPosition - range);
    const end = Math.min(text.length, targetPosition + range);

    for (let i = start; i <= end; i++) {
      if (CursorUtils.isValidCursorPosition(i, text, fieldType)) {
        validPositions.push(i);
      }
    }

    return validPositions;
  }

  /**
   * Convert between cursor position and raw character position
   * 
   * @param position Position in formatted text
   * @param text Formatted text
   * @param fieldType Field type
   * @param toRaw Whether converting to raw position (or from raw)
   * @returns Converted position
   */
  static convertCursorPosition(
    position: number,
    text: string,
    fieldType: FieldType,
    toRaw: boolean = true
  ): number {
    const delimiters = CursorUtils.getDelimitersForFieldType(fieldType);
    
    if (toRaw) {
      // Convert from formatted to raw position
      let rawPosition = 0;
      for (let i = 0; i < Math.min(position, text.length); i++) {
        if (!delimiters.includes(text[i])) {
          rawPosition++;
        }
      }
      return rawPosition;
    } else {
      // Convert from raw to formatted position
      let formattedPosition = 0;
      let rawCount = 0;
      
      for (let i = 0; i < text.length && rawCount < position; i++) {
        if (!delimiters.includes(text[i])) {
          rawCount++;
        }
        formattedPosition++;
      }
      
      return formattedPosition;
    }
  }

  // Private utility methods

  /**
   * Get delimiters for field type
   */
  private static getDelimitersForFieldType(fieldType: FieldType): string[] {
    const delimiterMap: Record<FieldType, string[]> = {
      [FieldType.PHONE]: ['(', ')', ' ', '-'],
      [FieldType.ZIP]: ['-'],
      [FieldType.DATE_OF_BIRTH]: ['/'],
      [FieldType.SSN]: ['-'],
      [FieldType.CURRENCY]: ['$', ',', '.'],
      [FieldType.NAME]: [],
      [FieldType.UNKNOWN]: []
    };

    return delimiterMap[fieldType] || [];
  }

  /**
   * Create simple character mapping between texts
   */
  private static createSimpleCharacterMapping(
    originalText: string,
    newText: string,
    fieldType: FieldType
  ): Array<{ originalIndex: number; newIndex: number; char: string }> {
    const mapping: Array<{ originalIndex: number; newIndex: number; char: string }> = [];
    const delimiters = CursorUtils.getDelimitersForFieldType(fieldType);
    
    let originalIndex = 0;
    let newIndex = 0;
    
    // Create mapping for non-delimiter characters
    while (originalIndex < originalText.length && newIndex < newText.length) {
      const originalChar = originalText[originalIndex];
      const newChar = newText[newIndex];
      
      // Skip delimiters in new text
      if (delimiters.includes(newChar)) {
        newIndex++;
        continue;
      }
      
      // Skip delimiters in original text
      if (delimiters.includes(originalChar)) {
        originalIndex++;
        continue;
      }
      
      // Map character
      if (originalChar === newChar) {
        mapping.push({
          originalIndex,
          newIndex,
          char: originalChar
        });
      }
      
      originalIndex++;
      newIndex++;
    }
    
    return mapping;
  }

  /**
   * Get fallback cursor position
   */
  private static getFallbackPosition(text: string): CursorPosition {
    return {
      start: text.length,
      end: text.length,
      direction: 'none'
    };
  }
}

/**
 * Undo/Redo Cursor Manager
 * 
 * Manages cursor position for undo/redo operations.
 */
export class UndoRedoCursorManager {
  private cursorHistory: CursorPosition[] = [];
  private historyIndex: number = -1;
  private maxHistorySize: number = 50;

  /**
   * Save cursor position to history
   * 
   * @param position Cursor position to save
   */
  saveCursorPosition(position: CursorPosition): void {
    // Remove any history beyond current index
    this.cursorHistory = this.cursorHistory.slice(0, this.historyIndex + 1);
    
    // Add new position
    this.cursorHistory.push({ ...position });
    this.historyIndex++;
    
    // Maintain history size limit
    if (this.cursorHistory.length > this.maxHistorySize) {
      this.cursorHistory.shift();
      this.historyIndex--;
    }
  }

  /**
   * Get cursor position for undo operation
   * 
   * @returns Cursor position for undo
   */
  getUndoCursorPosition(): CursorPosition | null {
    if (this.historyIndex > 0) {
      this.historyIndex--;
      return { ...this.cursorHistory[this.historyIndex] };
    }
    return null;
  }

  /**
   * Get cursor position for redo operation
   * 
   * @returns Cursor position for redo
   */
  getRedoCursorPosition(): CursorPosition | null {
    if (this.historyIndex < this.cursorHistory.length - 1) {
      this.historyIndex++;
      return { ...this.cursorHistory[this.historyIndex] };
    }
    return null;
  }

  /**
   * Clear cursor history
   */
  clearHistory(): void {
    this.cursorHistory = [];
    this.historyIndex = -1;
  }

  /**
   * Get history statistics
   */
  getHistoryStats(): {
    size: number;
    index: number;
    canUndo: boolean;
    canRedo: boolean;
  } {
    return {
      size: this.cursorHistory.length,
      index: this.historyIndex,
      canUndo: this.historyIndex > 0,
      canRedo: this.historyIndex < this.cursorHistory.length - 1
    };
  }
}